import React, { useState } from 'react'
import { icons } from 'assets/icons'
import { PhoneIcon } from 'assets/icons/PhoneIcon'
import { MailIcon } from 'assets/icons/MailIcon'
import { useNavigate } from 'react-router-dom'
import { ProfileBadge } from './common/ProfileBadge'
import { BadgeContainer } from './common/BadgeContainer'
import { ProfilePrice } from './common/ProfilePrice'
import {
  MemberProfileShortType,
  VALID_COMPANY_ENTITIES,
  VALID_PEOPLE_ENTITIES,
  isFreeMembership,
  isPaidMembership,
} from 'common/types/MemberProfile'
import { getMembershipShortColors } from './common/membershipStyles'
import './short-member-profile.scss'
import { useViewCounts } from 'common/hooks/useViewCounts'
import { useFavourites } from 'common/hooks/useFavourites'

interface MemberProfileShortProps {
  profile: MemberProfileShortType
  className?: string
  removeFavourite?: (profileId: string) => void
  disableViewButton?: boolean
}

export const MemberProfileShort = ({
  profile,
  className,
  removeFavourite,
  disableViewButton,
}: MemberProfileShortProps) => {
  const navigate = useNavigate()
  const { increaseView } = useViewCounts()
  const { setFavourite } = useFavourites()
  const colors = getMembershipShortColors(profile.membership_type)
  const iconClasses =
    'bg-transparent text-white w-4 h-4 rounded-[4px] cursor-pointer hover:text-[#001A3D] hover:bg-white'
  const name = VALID_PEOPLE_ENTITIES.includes(profile.profile_type)
    ? `${profile.first_name} ${profile?.last_name}`
    : profile?.practice_name
  const initials = name?.split(' ').map((n) => n[0])

  const [questionMarkIcon, setQuestionMarkIcon] = useState(
    icons.light.question_mark,
  )
  const [isFavourite, setIsFavourite] = useState(profile?.is_favourite === true)
  const [isHovering, setIsHovering] = useState(false)
  const heartIcon = isFavourite ? icons.blue.heart : icons.light.heart

  const [showInfoPopup, setShowInfoPopup] = useState(false)
  const [showCostPopup, setShowCostPopup] = useState(false)

  const handleViewClick = () => {
    increaseView(profile?.id)
    const path = VALID_COMPANY_ENTITIES.includes(profile.profile_type)
      ? `/profile/${profile.profile_type}/${profile.practice_name}-${profile?.friendly_id}`
      : `/profile/${profile.profile_type}/${profile.first_name}-${profile.last_name}-${profile?.friendly_id}`
    navigate(path)
  }

  const handleFavoriteClick = async () => {
    try {
      const key =
        profile.profile_type === 'solicitor'
          ? 'lawyers'
          : profile.profile_type === 'chamber'
            ? 'chambers'
            : profile.profile_type === 'lawfirm'
              ? 'lawfirm'
              : 'barristers'

      await setFavourite(key, profile?.id)
      const newFavState = !isFavourite
      setIsFavourite(newFavState)
      if (removeFavourite) {
        removeFavourite(profile?.id)
      }
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <div
      className={`bg-white shadow-[0px_4px_12px_0px_rgba(0,0,0,0.10)] !rounded-[10px] relative w-[306px] h-[430px] ${className}`}
    >
      <div
        className='flex p-[15px_22px] !rounded-[10px] h-[162px] justify-between items-start'
        // TODO: this should really be put into taildwind config
        style={{
          backgroundImage: colors.headerBg,
          backgroundSize: '950px',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className='flex flex-col gap-5'>
          <a href={`mailto:${profile.email_address}`}>
            <MailIcon className={iconClasses} />
          </a>
          <a href={`tel:${profile?.phone_number}`}>
            <PhoneIcon className={iconClasses} />
          </a>
          {isFreeMembership(profile?.membership_type) && (
            <img
              src={questionMarkIcon}
              className='h-[16px] w-[16px]'
              alt='info icon'
              onMouseEnter={() => setQuestionMarkIcon(icons.dark.question_mark)}
              onMouseLeave={() =>
                setQuestionMarkIcon(icons.light.question_mark)
              }
            />
          )}
        </div>

        <div className='mt-3 text-center px-4'>
          <h1 className='text-base md:text-lg text-[#001A3D] font-medium md:font-bold'>
            {name}
          </h1>
          {isPaidMembership(profile?.membership_type) &&
            profile.years_of_experience >= 1 && (
              <p className='text-xs md:text-sm text-[#25395C]'>
                {profile?.years_of_experience} years of experience
              </p>
            )}
        </div>

        <div className='flex gap-5 flex-col justify-between items-center h-full'>
          <div className='flex flex-col items-center gap-5'>
            <img
              src={isHovering ? icons.dark.heart : heartIcon}
              className='h-[24px] w-[24px] cursor-pointer !max-w-none'
              alt='heart icon'
              onClick={handleFavoriteClick}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            />
            {isFreeMembership(profile?.membership_type) && (
              <img
                src={icons.Info}
                className='h-[12px] w-[12px]'
                alt='info icon'
                onMouseEnter={() => setShowInfoPopup(true)}
                onMouseLeave={() => setShowInfoPopup(false)}
              />
            )}
          </div>
        </div>
      </div>

      {showInfoPopup && (
        <div className='absolute top-[76px] right-4 bg-[#001A30] text-white text-xs rounded-lg p-4 shadow-lg w-[260px] border border-[#EBFBFF] z-10'>
          <p>
            The information presented here is provided for general informational
            purposes only and is sourced from third-party entities. While we
            strive to ensure the accuracy and reliability of the information
            provided, Legal Utopia makes no representations or warranties of any
            kind, express or implied, about the completeness, accuracy,
            reliability, suitability, or availability of the information.
          </p>
        </div>
      )}

      {isPaidMembership(profile?.membership_type) && (
        <div className='flex justify-end mt-[-24px] mr-[15px]'>
          <ProfileBadge variant='promoted' text='Promoted' />
        </div>
      )}

      <div
        className={`absolute left-[128px] top-[117px] flex mx-auto h-[44px] w-[44px] bg-gray-100 rounded-full border border-solid ${colors.initialsBorder} initials-border`}
      >
        {profile?.avatar_url ? (
          <img
            src={profile?.avatar_url}
            className='h-full w-full object-cover rounded-full transform rotate-[56deg]'
            alt=''
          />
        ) : (
          <h1 className='m-auto text-sm font-medium transform rotate-[56deg]'>
            {initials}
          </h1>
        )}
      </div>

      <div className='p-[10px_20px] h-[206px] flex flex-col justify-between'>
        {profile.bio ? (
          <p className='text-xs xl:text-sm text-center text-[#586885] font-[500] my-[10px] min-h-20'>
            Short bio:&nbsp;
            {profile.bio?.slice(0, 120) +
              (profile.bio?.length > 120 ? '...' : '')}
          </p>
        ) : (<div className='flex-grow'></div>)}
        <div>
          <hr className='text-[#F4F4F4] my-[10px]' />
          <div>
            <BadgeContainer title='Areas of law'>
              {profile?.areas_of_law?.length > 0 ? (
                <>
                  {profile.areas_of_law
                    .sort((a, b) => a.length - b.length)
                    .slice(0, 2)
                    .map((text: string) => (
                      <ProfileBadge
                        text={text}
                        variant='areas_of_law'
                        key={text}
                      />
                    ))}
                  {profile.areas_of_law.length > 2 && (
                    <ProfileBadge
                      text={`+${profile.areas_of_law.length - 2} more`}
                      variant='areas_of_law'
                    />
                  )}
                </>
              ) : (
                <ProfileBadge text='Not provided' variant='areas_of_law' />
              )}
            </BadgeContainer>
          </div>
        </div>
      </div>
      <div
        className={`relative h-[62px] rounded-b-[10px] p-5 ${colors.footerBg}`}
      >
        <div className='flex justify-between items-center'>
          <div>
            {profile.hourly_rate > 0 && (
              <div className='flex gap-2'>
                <ProfilePrice
                  hourlyRate={profile.hourly_rate}
                  className='text-white'
                />
                {isFreeMembership(profile.membership_type) && (
                  <img
                    src={icons.Info}
                    className='w-3 h-3 -mt-[8px]'
                    alt='info icon'
                    onMouseEnter={() => setShowCostPopup(true)}
                    onMouseLeave={() => setShowCostPopup(false)}
                  />
                )}
              </div>
            )}
          </div>
          {isPaidMembership(profile.membership_type) && (
            <button
              type='submit'
              className={`btn-default text-[#1C7D93] font-medium text-xs w-[45px] h-[24px] rounded-4px ${disableViewButton ? 'null' : 'hover:bg-slate-300'}`}
              disabled={disableViewButton}
              onClick={handleViewClick}
            >
              View
            </button>
          )}
          {showCostPopup && (
            <div className='absolute bottom-[5px] right-[20px] bg-[#001A3D] text-white text-xs rounded-lg p-4 shadow-lg w-[250px] border border-[#EBFBFF] z-10'>
              <p>
                The median value of other providers in the same location,
                services, and areas of law. Actual hourly cost may be different
                and subject to VAT.
              </p>
            </div>
          )}
        </div>
        {isFreeMembership(profile.membership_type) &&
          profile.hourly_rate > 0 && (
            <p className='text-[8px] font-[400] text-white mt-[-3px]'>
              Estimated cost
            </p>
          )}
      </div>
    </div>
  )
}
